import LandingNavbar, {
  LandingFooter,
} from "@/features/landing/components/LandingNavbar";
import TableOfContents, {
  type TableOfContentsSection,
} from "@/components/Common/TableOfContents";
import { useEffect, useState } from "react";
import gsap from "gsap";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import {
  IntroductionSection,
  DefinitionsSection,
  LicenseSection,
  PlatformEligibilitySection,
  AcceptableUsePolicySection,
  PrivacyDataProtectionSection,
  IntellectualPropertySection,
  ServiceAvailabilitySection,
  ModificationOfTermsSection,
  GoverningLawSection,
  TerminationSection,
  LimitationOfLiabilitySection,
  IndemnificationSection,
  DisclaimerSection,
  ContactSection,
} from "../components/TermsSections";

const termsSections: TableOfContentsSection[] = [
  {
    title: "Introduction",
    id: "introduction",
  },
  {
    title: "Definitions",
    id: "definitions",
  },
  {
    title: "License",
    id: "license",
  },
  {
    title: "Platform Eligibility Statement",
    id: "platform-eligibility",
    items: [
      { label: "Eligibility", id: "eligibility" },
      { label: "Registration Information", id: "registration-info" },
      { label: "Account Security", id: "account-security" },
    ],
  },
  {
    title: "Acceptable Use Policy",
    id: "acceptable-use",
    items: [
      { label: "Respect and Conduct", id: "respect-conduct" },
      { label: "Security and Integrity", id: "security-integrity" },
    ],
  },
  {
    title: "Privacy and Data Protection",
    id: "privacy-data-protection",
    items: [
      { label: "Commitment to Privacy", id: "commitment-to-privacy" },
      { label: "Data Security", id: "data-security" },
    ],
  },
  {
    title: "Intellectual Property",
    id: "intellectual-property",
    items: [
      { label: "Ownership of Metawhale IP", id: "ownership" },
      { label: "User Content", id: "user-content" },
      { label: "Restrictions on Use", id: "restrictions" },
      { label: "Media Content", id: "media-content" },
      { label: "Feedback", id: "feedback" },
      { label: "Termination of Rights", id: "termination-of-rights" },
    ],
  },
  {
    title: "Service Availability and Functionality",
    id: "service-availability",
    items: [
      { label: "Service Uptime", id: "service-uptime" },
      { label: "Media Coverage Variations", id: "media-coverage-variations" },
      { label: "Disclaimer of Warranties", id: "disclaimer-of-warranties" },
      { label: "Support and Maintenance", id: "support-and-maintenance" },
    ],
  },
  {
    title: "Modification of Terms",
    id: "modification-of-terms",
    items: [
      { label: "Notification of Changes", id: "notification-of-changes" },
      { label: "User Consent", id: "user-consent" },
      { label: "Significant Changes", id: "significant-changes" },
    ],
  },
  {
    title: "Governing Law and Dispute Resolution",
    id: "governing-law",
  },
  {
    title: "Termination",
    id: "termination",
  },
  {
    title: "Limitation of Liability",
    id: "limitation-of-liability",
    items: [
      { label: "Scope and Cap of Liability", id: "scope-and-cap-of-liability" },
      { label: "Legal Constraints", id: "legal-constraints" },
      { label: "Service Disclaimer", id: "service-disclaimer" },
      {
        label: "Acknowledgment and Severability",
        id: "acknowledgment-and-severability",
      },
    ],
  },
  {
    title: "Indemnification",
    id: "indemnification",
  },
  {
    title: "Disclaimer",
    id: "disclaimer",
  },
  {
    title: "Contact",
    id: "contact",
  },
];

export default function TermsConditionsContainer() {
  const [isScrolled, setIsScrolled] = useState(false);

  const [scrollTop, setScrollTop] = useState(0);

  useEffect(() => {
    const scroller = document.querySelector(".terms-conditions-scroll");
    if (!scroller) return;

    const onScroll = () => {
      setIsScrolled(scroller.scrollTop > 10);
      setScrollTop(scroller.scrollTop);
    };

    scroller.addEventListener("scroll", onScroll);
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  gsap.registerPlugin(ScrollToPlugin);

  useEffect(() => {
    const scroller = document.querySelector(".terms-conditions-scroll");
    if (!scroller) return;

    const onScroll = () => {
      setIsScrolled(scroller.scrollTop > 10);
    };

    scroller.addEventListener("scroll", onScroll);

    return () => {
      scroller.removeEventListener("scroll", onScroll);
    };
  }, []);
  return (
    <div className="terms-conditions-scroll scrollbar-thin relative flex h-screen w-full flex-col overflow-x-hidden overflow-y-auto scroll-smooth bg-neutral-50">
      <LandingNavbar isScrolled={isScrolled} getInTouch={true} />

      <div className="relative -mt-[80px] flex w-full flex-1 transition-all duration-300">
        <div className="pointer-events-none fixed top-5 left-70">
          <div
            className="h-[700px] w-[200px] rounded-full"
            style={{
              background:
                "radial-gradient(circle, #EA580CBF 0%, transparent 100%)",
              filter: "blur(120px)",
              opacity: "25%",
            }}
          />
        </div>
        {/* Main Content */}
        <div className="flex-1 space-y-20 px-[400px] py-[200px]">
          <div className="w-full space-y-10">
            <h1 className="text-7xl leading-20 font-extrabold -tracking-wide text-neutral-950">
              Terms of Service
            </h1>
            <div className="text-medium text-md w-full max-w-[640px] space-y-6 leading-normal text-neutral-800">
              <p>
                Your use of the Metawhale Platform (the "Platform") and the
                services provided constitute your agreement to be bound by these
                Terms of Service. By accessing and placing an order with the
                Platform, you confirm that you agree with and are bound by these
                terms.
              </p>
              <p>
                <span className="font-bold">Last Updated</span>: January 1, 2026
              </p>
            </div>
          </div>
          <div className="w-full max-w-[640px] space-y-6">
            <IntroductionSection />
            <DefinitionsSection />
            <LicenseSection />
            <PlatformEligibilitySection />
            <AcceptableUsePolicySection />
            <PrivacyDataProtectionSection />
            <IntellectualPropertySection />
            <ServiceAvailabilitySection />
            <ModificationOfTermsSection />
            <GoverningLawSection />
            <TerminationSection />
            <LimitationOfLiabilitySection />
            <IndemnificationSection />
            <DisclaimerSection />
            <ContactSection />
          </div>
        </div>

        <div
          className="sticky right-50 h-fit w-[300px] flex-shrink-0 overflow-y-auto rounded-[12px] bg-neutral-100 p-6"
          style={{
            top: `${Math.max(100, 200 - scrollTop)}px`,
            transition: "top 0.25s ease-out",
          }}
        >
          <TableOfContents sections={termsSections} scrollOffset={80} />
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}
